import dayjs from "dayjs";

import { createContext, PropsWithChildren, useCallback, useContext, useState } from "react";

import {
  ISO_FORMAT,
  LOCALIZED_DATE_FORMAT,
  LOCALIZED_DATETIME_FORMAT,
  LOCALIZED_DATETIME_FORMAT_SHORT,
  LOCALIZED_DATETIME_NO_YEAR,
  LOCALIZED_TIME_FORMAT,
  LOCALIZED_TIME_FORMAT_SHORT
} from "./consts";
import { convertToISO } from "../../../../common/src/lib/formatDate/formatDate";

export type Timezone = "default" | "UTC";

export interface FormatDateOptions {
  includeSeconds?: boolean;
  addOneHour?: boolean;
  format?: "date" | "time" | "datetime" | "short" | "iso";
}

interface TimezoneContextType {
  timezone: Timezone;
  setTimezone: (tz: Timezone) => void;
  formatDate: (date: string | number, options?: FormatDateOptions) => string;
}

export const TimezoneContext = createContext<TimezoneContextType>({
  timezone: "default",
  setTimezone: () => {
    return;
  },
  formatDate: () => ""
});

const TIMEZONE_KEY = "timezone";

const getTimezoneFromLocalStorage = (): Timezone => {
  const timezone = localStorage.getItem(TIMEZONE_KEY) as Timezone;

  if (timezone === "UTC") {
    dayjs.tz.setDefault(timezone);
  }

  return timezone || "default";
};

const setTimezoneInLocalStorage = (tz: Timezone) => {
  localStorage.setItem(TIMEZONE_KEY, tz);
};

export const TimezoneProvider = ({ children }: PropsWithChildren) => {
  const [timezone, setTimezoneInStore] = useState<Timezone>(getTimezoneFromLocalStorage());

  const setTimezone = useCallback(
    (tz: Timezone) => {
      setTimezoneInStore(tz);
      setTimezoneInLocalStorage(tz);

      if (tz === "UTC") {
        // Set the default timezone to UTC
        dayjs.tz.setDefault(tz);
      } else {
        // Reset the default timezone back to local
        dayjs.tz.setDefault();
      }
    },
    [setTimezoneInStore]
  );

  const formatDate = useCallback(
    (date: string | number, options?: FormatDateOptions) => {
      const { includeSeconds = false, addOneHour = false, format = "datetime" } = options || {};

      if (!date) {
        return "";
      }

      const day = dayjs.utc(convertToISO(date));
      let formatOption;

      switch (format) {
        case "datetime":
          formatOption = includeSeconds
            ? LOCALIZED_DATETIME_FORMAT
            : LOCALIZED_DATETIME_FORMAT_SHORT;
          break;
        case "date":
          formatOption = LOCALIZED_DATE_FORMAT;
          break;
        case "time":
          formatOption = includeSeconds ? LOCALIZED_TIME_FORMAT : LOCALIZED_TIME_FORMAT_SHORT;
          break;
        case "short":
          formatOption = LOCALIZED_DATETIME_NO_YEAR;
          break;
        case "iso":
          formatOption = ISO_FORMAT;
          break;
        default:
          break;
      }

      if (addOneHour) {
        day.add(1, "hour");
      }

      if (timezone === "UTC") {
        return day.format(formatOption);
      } else {
        return day.local().format(formatOption);
      }
    },
    [timezone]
  );

  return (
    <TimezoneContext.Provider
      value={{
        timezone,
        setTimezone,
        formatDate
      }}>
      {children}
    </TimezoneContext.Provider>
  );
};

export const useTimezoneContext = () => {
  const context = useContext(TimezoneContext);

  if (!context) {
    throw new Error("useTimezoneContext must be used within a TimezoneProvider");
  }

  return context;
};
